import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";
import { identifyUser, resetUser } from "@/lib/posthog";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  providerId: string | null;
  affiliateAccountId: string | null;
  role: string | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  providerId: null,
  affiliateAccountId: null,
  role: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [affiliateAccountId, setAffiliateAccountId] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const roleLoadedRef = useRef(false);

  const fetchUserRole = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role, provider_id, affiliate_account_id")
      .eq("user_id", userId)
      .maybeSingle() as { data: { role: string; provider_id: string | null; affiliate_account_id: string | null } | null };

    if (data) {
      setRole(data.role);
      setProviderId(data.provider_id);
      setAffiliateAccountId(data.affiliate_account_id ?? null);
      identifyUser(userId, { role: data.role, provider_id: data.provider_id });
    } else {
      // No role found — auto-provision as guest
      try {
        await supabase.rpc("provision_guest_profile");
        setRole("guest");
      } catch {
        // If provisioning fails, still continue
      }
    }
    roleLoadedRef.current = true;
    setLoading(false);
  };

  useEffect(() => {
    // When the URL contains a recovery token, skip role-fetch so the
    // reset-password page can handle the PASSWORD_RECOVERY event.
    const isRecoveryFlow = window.location.hash.includes("type=recovery");

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user && !isRecoveryFlow) {
          // Use setTimeout to avoid Supabase client deadlock, but only
          // set loading=false AFTER role is fetched
          setTimeout(() => fetchUserRole(session.user.id), 0);
        } else if (session?.user && isRecoveryFlow) {
          setLoading(false);
        } else {
          setRole(null);
          setProviderId(null);
          setAffiliateAccountId(null);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user && !isRecoveryFlow) {
        // fetchUserRole will setLoading(false) when done
        setTimeout(() => {
          if (!roleLoadedRef.current) {
            fetchUserRole(session.user.id);
          }
        }, 0);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    resetUser();
    setUser(null);
    setSession(null);
    setRole(null);
    setProviderId(null);
    setAffiliateAccountId(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, providerId, affiliateAccountId, role, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
